import clsx from 'clsx';
import { Fragment, isValidElement } from 'react';

import { LegacyColorMap } from '@/theme/LegacyColorMap';
import { Colors } from '../../../../theme';
import type { AnchorTarget } from '../../../../ts-types';
import { Heading } from '../../../display/Heading';
import type { IconName } from '../../../display/Icon';
import { Icon } from '../../../display/Icon';
import { Tag } from '../../../display/Tag';
import { Text } from '../../../display/Text';
import { Grid } from '../../../layout/Grid';
import { LegacyButton } from '../../../navigation/LegacyButton';
import { SubscriptionFormWithDisclaimer } from '../../SubscriptionForm';
import { CTAColor, HeroColorsV4, HeroColorsV5 } from '../types';
import {
  getSupportedCTAColor,
  mapBackgroundColorToV5HeadingColorName,
  mapBackgroundColorToV5TextColorName,
  transformBackgroundColor,
  validateZapierBackgroundAccessibility,
} from '../utils';

import styles from './HeroStandard.module.scss';

export type Props = {
  /**
   * Controls the background of the entire element.
   *
   * Must be one of type `'lava60' | 'ocean40' | 'sun60' | 'white' | 'neutral100' | 'zapier' | 'night' | 'moss' | 'neutral800'`.
   *
   * It also controls the color of the CTA button - background color
   * is white, then the CTA button is `primary`. Otherwise, it is `primary-alt`
   * */
  backgroundColor?: HeroColorsV4 | HeroColorsV5;
  /**
   * Heading text content.
   */
  heading: React.ReactNode;
  /**
   * Text content displayed below the heading.
   */
  description?: React.ReactNode;
  /**
   * Whether the subscription form is enabled in place of CTA button.
   */
  isSubscriptionForm?: boolean;
  /**
   * Placeholder text to display inside the subscription form input field.
   */
  subscriptionPlaceholder?: string;
  /**
   * Text content of the CTA button.
   */
  ctaText?: React.ReactNode;
  /**
   * Background color of the CTA button.
   */
  ctaColor?: CTAColor;
  /**
   * href for the CTA button.
   */
  ctaHref?: string;
  /**
   * The `target` attribute for the CTA button.
   */
  ctaTarget?: AnchorTarget;
  /**
   * Whether the CTA button is disabled
   */
  ctaDisabled?: boolean;
  /**
   * Text displayed next to CTA button.
   */
  ctaDisclaimer?: React.ReactNode;
  /**
   * CTA button click handler.
   */
  onCtaClick?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  /**
   * CTA button keyDown handler.
   */
  onCtaKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  /**
   * max-width in pixels applied to the content area of container.
   */
  maxWidth?: number;
  /**
   * Renders in the space opposite the text.
   * Can be used to render media such as images, videos, or any other React Component. */
  children?: React.ReactNode;
  /**
   * Function that gets called on subscription form submit.
   */
  onSubscriptionFormSubmit?: (subscriptionValue?: string) => void;
  /**
   * A label that renders as a small tag
   * with a background color and an icon
   */
  label?: string;
  /**
   * The icon that is displayed in the label.
   */
  labelIcon?: IconName;
  /**
   * The labelColor determines the background-color of the label.
   */
  labelColor?:
    | 'default'
    | 'success'
    | 'error'
    | 'warning'
    | 'inverted'
    | 'simple';
};

/**
 * `HeroStandard` is used as a large promotional section towards the top of a page. It features a heading, description text, a Call To Action (CTA), and media, which is rendered as `children`.
 */
export const HeroStandard = ({
  backgroundColor = 'neutral100',
  ctaColor = 'primary',
  isSubscriptionForm = false,
  subscriptionPlaceholder = 'Email address',
  heading,
  description,
  ctaText,
  ctaHref,
  ctaTarget,
  ctaDisabled = false,
  ctaDisclaimer,
  onCtaClick,
  onCtaKeyDown,
  onSubscriptionFormSubmit,
  children,
  maxWidth = 1170,
  label,
  labelIcon,
  labelColor,
}: Props) => {
  const mappedBackgroundColor = transformBackgroundColor(backgroundColor);

  // This should validate and through a console error as the disclaimer text accessibility
  // is poor on the zapier background.
  validateZapierBackgroundAccessibility(
    mappedBackgroundColor,
    isSubscriptionForm,
    !!ctaDisclaimer
  );

  const getCtaComponent = () => {
    const color = getSupportedCTAColor(ctaColor, mappedBackgroundColor);
    // Load-bearing <div>; Prevents the button from blowing out the flex layout
    // in certain scenarios. Better idea: refactor the CSS to use grid instead
    // of flexbox, allowing direct control of child sizes.
    return (
      <div>
        <LegacyButton
          children={ctaText}
          href={!isSubscriptionForm ? ctaHref : undefined}
          onClick={onCtaClick}
          onKeyDown={onCtaKeyDown}
          isFullWidth={true}
          size={isSubscriptionForm ? 'medium' : 'large'}
          type={isSubscriptionForm ? 'submit' : 'button'}
          target={ctaTarget}
          disabled={ctaDisabled}
          color={color === 'primary-alt' ? 'primary' : color}
        />
      </div>
    );
  };

  const isDisclaimerElement = !!ctaDisclaimer && isValidElement(ctaDisclaimer);

  return (
    <div
      className={styles.root}
      style={{
        '--_zds-hero-background-color':
          Colors[LegacyColorMap[mappedBackgroundColor]],
        '--_zds-hero-heading-color':
          Colors[mapBackgroundColorToV5TextColorName(mappedBackgroundColor)],
        '--_zds-hero-description-color':
          Colors[mapBackgroundColorToV5HeadingColorName(mappedBackgroundColor)],
        '--_zds-hero-content-width': `${maxWidth}px`,
      }}
      data-zds
    >
      <div className={styles.content} data-zds>
        <div className={styles.textContent} data-zds>
          {label && (
            <div className={styles.labelContainer} data-zds>
              <Tag variant={labelColor}>
                {labelIcon ? (
                  <Grid alignItems="center" columns="auto max-content" gap={5}>
                    <Icon name={labelIcon} size={20} />
                    {label}
                  </Grid>
                ) : (
                  label
                )}
              </Tag>
            </div>
          )}
          <Heading className={styles.heading}>{heading}</Heading>
          {description && (
            <div className={styles.description} data-zds>
              {description}
            </div>
          )}
          {ctaText && (ctaHref || onCtaClick || isSubscriptionForm) && (
            <div
              className={clsx(
                styles.ctaContainer,
                isSubscriptionForm && styles.subscriptionForm
              )}
              data-zds
            >
              {isSubscriptionForm ? (
                <SubscriptionFormWithDisclaimer
                  placeholderText={subscriptionPlaceholder}
                  onSubmit={onSubscriptionFormSubmit}
                  renderButton={() => getCtaComponent()}
                  disclaimerTextColor={
                    mappedBackgroundColor === 'neutral100'
                      ? 'primary'
                      : 'secondary'
                  }
                />
              ) : (
                <Fragment>
                  <div data-zds>{getCtaComponent()}</div>
                  {ctaDisclaimer && (
                    <div
                      className={clsx(
                        styles.ctaDisclaimer,
                        isSubscriptionForm && styles.subscriptionFormDisclaimer
                      )}
                      data-zds
                    >
                      {isDisclaimerElement ? (
                        ctaDisclaimer
                      ) : (
                        <Text
                          tag="p"
                          type="Body1"
                          color={mapBackgroundColorToV5HeadingColorName(
                            mappedBackgroundColor
                          )}
                        >
                          {ctaDisclaimer}
                        </Text>
                      )}
                    </div>
                  )}
                </Fragment>
              )}
            </div>
          )}
        </div>
        <div className={styles.mediaContent} data-zds>
          {children}
        </div>
      </div>
    </div>
  );
};
